/**
 * Scroll Memory
 * =============
 * Merkt sich die exakte Scroll-Position der Index-Seite und stellt sie wieder her,
 * wenn man von einer Detailseite (Stolperstein/Medienprojekt/Filmproduktion) zurückkommt.
 * So landet man wieder genau dort, wo man war – statt nur am Anfang der Section.
 */

const KEY = 'index-scroll-y';

function isMainPage(): boolean {
  const p = window.location.pathname;
  return p.endsWith('/') || p.endsWith('/index.html');
}

function cameFromDetailPage(): boolean {
  const ref = document.referrer;
  if (!ref) return false;
  try {
    const url = new URL(ref);
    if (url.origin !== window.location.origin) return false;
    return /\/(stolperstein|medienprojekt|filmproduktion)\//.test(url.pathname);
  } catch {
    return false;
  }
}

/**
 * Auf der Index-Seite die Scroll-Position sichern, sobald die Seite verlassen wird.
 */
export function initScrollMemory(): void {
  if (!isMainPage()) return;

  // Scroll-Wiederherstellung uebernehmen wir selbst (verhindert doppeltes Springen)
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  const save = (): void => {
    try {
      sessionStorage.setItem(KEY, String(Math.round(window.scrollY)));
    } catch {
      /* sessionStorage nicht verfuegbar - dann eben ohne Persistenz */
    }
  };

  // pagehide feuert zuverlaessig bei jeder Navigation weg von der Seite (inkl. bfcache)
  window.addEventListener('pagehide', save);
}

/**
 * Beim Laden der Index-Seite die exakte Position wiederherstellen,
 * wenn man von einer Detailseite zurueckkommt.
 * @returns true, wenn die Position wiederhergestellt wurde (Anker-Fallback dann ueberspringen).
 */
export function restoreScrollMemory(): boolean {
  if (!isMainPage() || !cameFromDetailPage()) return false;

  // Expliziter Anker (z. B. Footer-Link index.html#spenden) hat Vorrang vor der gemerkten Position
  if (window.location.hash.length > 1) return false;

  let saved: number | null = null;
  try {
    const v = sessionStorage.getItem(KEY);
    saved = v !== null ? parseInt(v, 10) : null;
  } catch {
    saved = null;
  }

  if (saved === null || Number.isNaN(saved) || saved <= 0) return false;

  const y = saved;

  // Beim Zurueckkehren hart springen (keine Animation), obwohl global scroll-behavior: smooth gilt
  const rootStyle = document.documentElement.style;
  const prevBehavior = rootStyle.scrollBehavior;
  rootStyle.scrollBehavior = 'auto';

  const restore = (): void => window.scrollTo(0, y);

  // Mehrfach ueber kurze Zeit anwenden, bis das Layout (nachgeladene Bilder etc.) stabil ist
  restore();
  requestAnimationFrame(restore);
  setTimeout(restore, 150);
  setTimeout(() => {
    restore();
    // urspruengliches Scrollverhalten (smooth) wiederherstellen
    rootStyle.scrollBehavior = prevBehavior;
  }, 350);

  // URL saeubern (falls returnTo/Hash gesetzt war)
  window.history.replaceState({}, '', window.location.pathname);

  return true;
}
